import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { db } from '../utils/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { PageContent } from '../types';
import { Loader } from 'lucide-react';
import { sanitizeHtml } from '../utils/sanitize';

const SECTIONS = [
  { id: 'contact', title: 'Contact Us' },
  { id: 'returns', title: 'Returns & Exchanges' },
  { id: 'faq', title: 'FAQ' },
  { id: 'privacy', title: 'Privacy Policy' },
  { id: 'terms', title: 'Terms of Use' }
];

const processContent = (rawContent: string) => {
  if (!rawContent) return '';
  // If the content already contains common block HTML tags, assume it's pre-formatted HTML
  if (/<(p|div|h[1-6]|ul|ol|li|table|section|article)/i.test(rawContent)) {
    return sanitizeHtml(rawContent);
  }

  // Otherwise, treat as plain text: split double newlines into paragraphs, single into breaks
  const formatted = rawContent
    .split(/\n\n+/)
    .map(paragraph => {
      if (paragraph.trim() === '---') return '<hr />';
      // Check if it's a short line that might be a heading (all caps or sentence case, no punctuation at end, less than 60 chars)
      const trimmed = paragraph.trim();
      const isHeading = trimmed.length > 0 && trimmed.length < 60 && !/[.!?]$/.test(trimmed) && trimmed.split('\n').length === 1 && !/^Do you|^Can I|^What /i.test(trimmed);

      if (isHeading) {
        return `<h2>${trimmed}</h2>`;
      }
      return `<p>${trimmed.replace(/\n/g, '<br/>')}</p>`;
    })
    .join('\n');

  return sanitizeHtml(formatted);
};

const PROSE_CLASSES = `prose prose-sm md:prose-base max-w-none
  text-gray-700
  prose-headings:font-heading prose-headings:font-bold prose-headings:uppercase prose-headings:tracking-tighter prose-headings:text-[#111]
  prose-h1:text-4xl prose-h1:mb-8
  prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-6 prose-h2:pb-2 prose-h2:border-b prose-h2:border-gray-100
  prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-4
  prose-p:font-body prose-p:leading-relaxed prose-p:mb-6
  prose-a:font-semibold prose-a:text-[#111] prose-a:underline prose-a:underline-offset-4 hover:prose-a:text-gray-500 prose-a:transition-colors
  prose-strong:font-bold prose-strong:text-[#111]
  prose-ul:list-disc prose-ul:pl-6 prose-ul:mb-6 prose-ul:marker:text-gray-400
  prose-ol:list-decimal prose-ol:pl-6 prose-ol:mb-6
  prose-li:mb-2 prose-li:font-body`;

// Single page: every section is rendered with an anchor, and the sidebar
// (plus ?section= links from the footer and cookie banner) scrolls to it.
const SupportPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedSection = searchParams.get('section');

  const [contents, setContents] = useState<Record<string, PageContent | null>>({});
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState(requestedSection || SECTIONS[0].id);
  const hasScrolledRef = useRef(false);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const entries = await Promise.all(
          SECTIONS.map(async (section) => {
            try {
              const docSnap = await getDoc(doc(db, 'page_content', section.id));
              return [section.id, docSnap.exists() ? (docSnap.data() as PageContent) : null] as const;
            } catch {
              return [section.id, null] as const;
            }
          })
        );
        setContents(Object.fromEntries(entries));
      } catch (error) {
        console.error("Error fetching support content:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, []);

  // Scroll to the requested section once content has rendered
  useEffect(() => {
    if (loading || !requestedSection || hasScrolledRef.current) return;
    hasScrolledRef.current = true;
    const el = document.getElementById(requestedSection);
    if (el) {
      setActiveSection(requestedSection);
      // Slight delay so layout settles before scrolling
      setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    }
  }, [loading, requestedSection]);

  const handleSectionChange = (id: string) => {
    setActiveSection(id);
    setSearchParams({ section: id });
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="bg-[#111] text-white pt-40 pb-20 px-4 md:px-8">
        <div className="max-w-[1200px] mx-auto text-center animate-slide-up">
          <h1 className="font-display text-5xl md:text-7xl font-normal uppercase tracking-wider mb-4">
            Support center
          </h1>
          <p className="font-body text-gray-400 max-w-xl mx-auto tracking-wide text-sm md:text-base">
            Everything you need to know about our products, policies, and how to get in touch.
          </p>
        </div>
      </div>

      <div className="py-20 px-4 md:px-8 max-w-[1200px] mx-auto">
        <div className="flex flex-col md:flex-row gap-12 lg:gap-24">

          {/* Sidebar Navigation */}
          <div className="md:w-64 shrink-0">
            <div className="md:sticky md:top-28">
              <h2 className="font-heading text-lg font-bold uppercase mb-6 tracking-widest text-[#111] border-b-2 border-black pb-4">
                Categories
              </h2>
              <div className="flex flex-col gap-1">
                {SECTIONS.map(section => (
                  <button
                    key={section.id}
                    onClick={() => handleSectionChange(section.id)}
                    className={`text-left text-sm font-bold uppercase tracking-widest py-3 px-4 transition-all duration-300 w-full ${
                      activeSection === section.id
                        ? 'bg-[#111] text-white'
                        : 'text-gray-500 hover:text-[#111] hover:bg-gray-100'
                    }`}
                  >
                    {section.title}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Content Area — all sections stacked with anchors */}
          <div className="flex-1 min-w-0">
            {loading ? (
               <div className="min-h-[400px] flex items-center justify-center">
                 <Loader className="animate-spin text-gray-400" size={32} />
               </div>
            ) : (
              <div className="animate-fade-in bg-white p-2 space-y-20">
                {SECTIONS.map(section => {
                  const data = contents[section.id];
                  const html = data?.content
                    ? processContent(data.content)
                    : '<p class="text-gray-500 italic">Content coming soon.</p>';
                  return (
                    <section key={section.id} id={section.id} className="scroll-mt-28">
                      <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tighter mb-10 text-[#111] border-b border-gray-200 pb-4">
                        {data?.title || section.title}
                      </h2>
                      <div
                        className={PROSE_CLASSES}
                        dangerouslySetInnerHTML={{ __html: html }}
                      />
                    </section>
                  );
                })}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default SupportPage;
